import { useState, useEffect, useRef } from 'react';

/**
 * Hook to monitor backend health status
 * Returns status: 'healthy' | 'warning' | 'error'
 */
const useBackendHealth = (checkInterval = 10000) => {
  const [status, setStatus] = useState('healthy'); // 'healthy', 'warning', 'error'
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const consecutiveFailures = useRef(0);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const apiBaseUrl = window.location.hostname === 'localhost' 
          ? 'http://localhost:8000'
          : window.location.origin;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
        
        const response = await fetch(`${apiBaseUrl}/health`, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          // Parse the health response
          const healthData = await response.json();
          
          // Check the comprehensive health status
          if (healthData.status === 'healthy' && healthData.checks?.database === 'ok') {
            // Fully healthy - reset counters
            consecutiveFailures.current = 0;
            setRetryCount(0);
            setStatus('healthy');
            setLastError(null);
          } else if (healthData.status === 'degraded' || healthData.checks?.database === 'degraded') {
            // Database issues but backend is responding
            consecutiveFailures.current += 1;
            setRetryCount(consecutiveFailures.current);
            
            if (consecutiveFailures.current >= 3) {
              setStatus('warning');
              setLastError('Database connectivity issues');
            }
          } else if (healthData.status === 'unhealthy' || healthData.checks?.database === 'error') {
            // Database error
            consecutiveFailures.current += 1;
            setRetryCount(consecutiveFailures.current);
            setStatus('error');
            setLastError(healthData.checks?.database_error || 'Database error');
          }
        } else if (response.status >= 500) {
          // Server error
          consecutiveFailures.current += 1;
          setRetryCount(consecutiveFailures.current);
          setStatus('error');
          setLastError(`Server error: ${response.status}`);
        } else {
          // Other errors (4xx)
          consecutiveFailures.current += 1;
          setRetryCount(consecutiveFailures.current);
          
          if (consecutiveFailures.current >= 3) {
            setStatus('warning');
          }
          setLastError(`HTTP ${response.status}`);
        }
      } catch (error) {
        // Network error or timeout
        consecutiveFailures.current += 1;
        setRetryCount(consecutiveFailures.current);
        
        if (consecutiveFailures.current >= 3) {
          setStatus(consecutiveFailures.current >= 5 ? 'error' : 'warning');
        }
        
        const errorMessage = error.name === 'AbortError' 
          ? 'Timeout' 
          : (error.message || 'Network error');
        
        setLastError(errorMessage);
        
        console.warn(`Backend health check failed (attempt ${consecutiveFailures.current}):`, errorMessage);
      }
    };

    // Check immediately on mount
    checkHealth();

    // Then check periodically
    const interval = setInterval(checkHealth, checkInterval);

    return () => clearInterval(interval);
  }, [checkInterval]);

  return {
    status,           // 'healthy' | 'warning' | 'error'
    retryCount,       // Number of consecutive failures
    lastError,        // Last error message
    isHealthy: status === 'healthy',
    isWarning: status === 'warning',
    isError: status === 'error'
  };
};

export default useBackendHealth;

